import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Grid,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import { format } from 'date-fns';

interface AggregateHealthChartProps {
  data: any;
  selectedProduct: string;
}

const COLORS = {
  green: '#4caf50',
  yellow: '#ff9800',
  red: '#f44336',
  primary: '#1976d2',
  secondary: '#9c27b0',
  grey: '#9e9e9e',
};

const AggregateHealthChart: React.FC<AggregateHealthChartProps> = ({
  data,
  selectedProduct
}) => {
  // Weekly %-in-control comes from the backend as a continuous 52-week series
  // (real calendar weeks, no gaps) that forward-fills each meter's most recent
  // proving and judges it against the maintenance-stepped baseline in effect that
  // week. Grouped by product (+ 'all') so the product filter just selects a key.
  const timeSeriesData = useMemo(() => {
    const wcs = data?.weekly_control_status;
    if (!wcs) return [];
    const series = wcs[selectedProduct] || wcs['all'] || [];
    return series.map((w: any) => ({
      weekStart: w.week_start,
      // `percentage` is OVERALL HEALTH, which is what this panel claims to show. It used to be
      // within-limits only, so a meter that failed repeatability — must be re-proved, must not be
      // used for custody transfer — sat inside a chart reporting 100%.
      controlPercentage: w.percentage,          // may be null (no active meters that week)
      healthy: w.healthy ?? w.in_control,       // older payloads only carried in_control
      inControl: w.in_control,
      inControlPercentage: w.in_control_percentage,
      active: w.active,
      // Parse as local midnight to avoid a UTC off-by-one on the label.
      date: format(new Date(`${w.week_start}T00:00:00`), 'MMM d, yyyy'),
    }));
  }, [data, selectedProduct]);

  // Health distribution data for pie chart
  // Four slices, and the split is the point: the top two count against the health score and the
  // bottom two do not. 97 of EET's warnings were one undifferentiated block, 80 of them merely a
  // proving backlog, which made the panel read as though half the fleet was in trouble.
  // `health_class` is computed by the aggregator with the SAME rules as the weekly health line, so
  // the pie and the chart cannot disagree.
  const healthDistribution = useMemo(() => {
    if (!data?.meters) return [];

    const metersToProcess = Object.entries(data.meters).filter(([key]) => {
      if (selectedProduct === 'all') return true;
      return key.split('_')[1] === selectedProduct;
    });

    let critical = 0, warnMeasurement = 0, warnAdmin = 0, inControl = 0;
    metersToProcess.forEach(([_, meterData]: [string, any]) => {
      const st = meterData.statistics || {};
      switch (st.health_class) {
        case 'critical': critical++; break;
        case 'warn_measurement': warnMeasurement++; break;
        case 'warn_admin': warnAdmin++; break;
        case 'in_control': inControl++; break;
        default:
          // Payload predates health_class — fall back to the coarse status.
          if (st.current_health === 'red') critical++;
          else if (st.current_health === 'yellow') warnAdmin++;
          else if (st.current_health === 'green') inControl++;
      }
    });

    return [
      { name: 'Critical', value: critical, color: COLORS.red, countsAgainstHealth: true },
      { name: 'Warning \u2013 Drift / Bias / Spike', value: warnMeasurement, color: COLORS.yellow, countsAgainstHealth: true },
      { name: 'Warning \u2013 Overdue / Adjustment', value: warnAdmin, color: '#cbb89a', countsAgainstHealth: false },
      { name: 'In Control', value: inControl, color: COLORS.green, countsAgainstHealth: false },
    ].filter((sl) => sl.value > 0);     // an empty slice is just legend clutter
  }, [data, selectedProduct]);

  // Meter trends histogram - showing drift distribution based on Cal AI 7-point rule
  const driftDistribution = useMemo(() => {
    if (!data?.meters) return [];

    const metersToProcess = Object.entries(data.meters).filter(([key]) => {
      if (selectedProduct === 'all') return true;
      return key.split('_')[1] === selectedProduct;
    });

    // Create buckets for consecutive trending events (drift detection)
    const buckets = [
      { range: '7+ Down', count: 0, color: COLORS.red, description: 'Warning - Drift Down' },
      { range: '5-6 Down', count: 0, color: COLORS.red, description: 'Critical - Trending Down' },
      { range: '3-4 Down', count: 0, color: COLORS.yellow, description: 'Needs Attention - Down' },
      { range: 'Stable (0-2)', count: 0, color: COLORS.green, description: 'Stable - Normal Variation' },
      { range: '3-4 Up', count: 0, color: COLORS.yellow, description: 'Needs Attention - Up' },
      { range: '5-6 Up', count: 0, color: COLORS.red, description: 'Critical - Trending Up' },
      { range: '7+ Up', count: 0, color: COLORS.red, description: 'Warning - Drift Up' },
    ];

    // Analyze each meter for consecutive trending events
    metersToProcess.forEach(([_, meterData]: [string, any]) => {
      const dataPoints = meterData.data || [];

      if (dataPoints.length < 2) {
        buckets[3].count++; // Count as stable if insufficient data
        return;
      }

      // Sort by timestamp
      const sorted = [...dataPoints].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      // Count consecutive trending events
      let consecutiveUp = 0;
      let consecutiveDown = 0;
      let maxConsecutiveUp = 0;
      let maxConsecutiveDown = 0;

      for (let i = 1; i < sorted.length; i++) {
        const change = sorted[i].meter_factor - sorted[i-1].meter_factor;

        if (Math.abs(change) > 0.0025) {
          // Point-to-point change exceeds threshold - this is a critical change, not drift
          // Reset consecutive counts as this breaks the trend pattern
          consecutiveUp = 0;
          consecutiveDown = 0;
        } else if (change > 0) {
          // Trending up
          consecutiveUp++;
          consecutiveDown = 0;
          maxConsecutiveUp = Math.max(maxConsecutiveUp, consecutiveUp);
        } else if (change < 0) {
          // Trending down
          consecutiveDown++;
          consecutiveUp = 0;
          maxConsecutiveDown = Math.max(maxConsecutiveDown, consecutiveDown);
        } else {
          // No change - reset both
          consecutiveUp = 0;
          consecutiveDown = 0;
        }
      }

      // Categorize based on maximum consecutive trends
      if (maxConsecutiveDown >= 7) {
        buckets[0].count++; // 7+ Down (Warning)
      } else if (maxConsecutiveDown >= 5) {
        buckets[1].count++; // 5-6 Down (Critical)
      } else if (maxConsecutiveDown >= 3) {
        buckets[2].count++; // 3-4 Down (Needs Attention)
      } else if (maxConsecutiveUp >= 7) {
        buckets[6].count++; // 7+ Up (Warning)
      } else if (maxConsecutiveUp >= 5) {
        buckets[5].count++; // 5-6 Up (Critical)
      } else if (maxConsecutiveUp >= 3) {
        buckets[4].count++; // 3-4 Up (Needs Attention)
      } else {
        buckets[3].count++; // Stable (0-2 drift)
      }
    });

    return buckets.map(b => ({
      name: b.range,
      value: b.count,
      trend: b.range,
      fill: b.color,
      description: b.description
    }));
  }, [data, selectedProduct]);

  // The score reads the LATEST point of the weekly health series below, so the headline and the
  // chart cannot disagree — they are the same number by construction.
  //
  // It used to be green / total, which counts every warning against the fleet. On EET that meant 94
  // meters merely OVERDUE for proving dragged it to 52% red "Needs Attention" while every meter sat
  // inside its control limits. A proving backlog is an administrative matter, not a measurement
  // fault, and burying one genuinely critical meter under 94 of them is what made the number
  // useless. Unhealthy here means outside limits, failed repeatability, spike, drift or bias.
  const healthScore = useMemo(() => {
    const series = timeSeriesData;
    for (let i = series.length - 1; i >= 0; i--) {
      const v = series[i]?.controlPercentage;
      if (v != null && Number.isFinite(Number(v))) return Number(v).toFixed(1);
    }
    // No weekly series (a dataset with no proving history): fall back to the old definition
    // rather than showing nothing.
    if (!healthDistribution.length) return '0.0';
    const total = healthDistribution.reduce((sum, item) => sum + item.value, 0);
    // The two slices that do NOT count against health are the score's numerator.
    const ok = healthDistribution.filter((sl) => !sl.countsAgainstHealth)
                                 .reduce((sum, sl) => sum + sl.value, 0);
    return (total > 0 ? (ok / total) * 100 : 0).toFixed(1);
  }, [timeSeriesData, healthDistribution]);

  // Percentages that reconcile. Two problems had to be solved together:
  //   1. a real count must never read 0% — 1 of 204 is 0.49%, which rounds to zero at 0dp;
  //   2. the rows must sum to 100% — mixed precision gave "0.49 + 48 + 52" = 100.49.
  // So ONE precision is chosen for every row, driven by the smallest non-zero slice, and the
  // rounding uses largest-remainder so the displayed figures add up exactly rather than
  // approximately.
  const sharePct = useMemo(() => {
    const total = healthDistribution.reduce((sum, sl) => sum + sl.value, 0);
    if (!total) return healthDistribution.map(() => '');
    const exact = healthDistribution.map((sl) => (sl.value / total) * 100);
    const smallest = Math.min(...exact.filter((p) => p > 0));
    const dp = smallest < 1 ? 2 : smallest < 10 ? 1 : 0;
    const step = 10 ** dp;                       // work in whole units of the last digit
    const scaled = exact.map((p) => p * step);
    const floored = scaled.map(Math.floor);
    let left = Math.round(100 * step) - floored.reduce((a, b) => a + b, 0);
    const order = scaled
      .map((v, i) => ({ i, rem: v - Math.floor(v) }))
      .sort((a, b) => b.rem - a.rem);
    const out = [...floored];
    for (let k = 0; k < order.length && left > 0; k++, left--) out[order[k].i] += 1;
    return out.map((v) => `${(v / step).toFixed(dp)}%`);
  }, [healthDistribution]);

  // Which slice is hovered — set from the pie AND from the legend, so pointing at a legend row
  // highlights its slice. With 97 and 106 dominating, the 1-meter Critical slice is a sliver that
  // is otherwise impossible to hit with a mouse.
  const [activeSlice, setActiveSlice] = useState<number | null>(null);
  const healthTotal = useMemo(
    () => healthDistribution.reduce((sum, sl) => sum + sl.value, 0), [healthDistribution]);

  const healthScoreNumber = parseFloat(healthScore);

  const radialData = [{
    name: 'Health Score',
    value: healthScoreNumber,
    fill: healthScoreNumber >= 80 ? COLORS.green : healthScoreNumber >= 60 ? COLORS.yellow : COLORS.red,
  }];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Grid container spacing={2}>
        {/* Meter Health Score */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Meter Health Score
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="45%" outerRadius="75%" data={radialData}>
                <RadialBar dataKey="value" cornerRadius={10} fill={radialData[0].fill} background={{ fill: '#f0f0f0' }} />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
                  <tspan x="50%" dy="-0.1em" fontSize="26" fontWeight="bold" fill="#333">
                    {healthScore}%
                  </tspan>
                  <tspan x="50%" dy="1.8em" fontSize="11" fill="#666">
                    Health Score
                  </tspan>
                </text>
              </RadialBarChart>
            </ResponsiveContainer>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Chip
                label={healthScoreNumber >= 80 ? 'Excellent' : healthScoreNumber >= 60 ? 'Good' : 'Needs Attention'}
                size="small"
                color={healthScoreNumber >= 80 ? 'success' : healthScoreNumber >= 60 ? 'warning' : 'error'}
              />
            </Stack>
          </Paper>
        </Grid>

        {/* Health Distribution */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Health Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={healthDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  // Labels sit INSIDE the slice. Outside labels were being clipped by the
                  // container, which is why the 97 never appeared at all. A slice under ~7% has no
                  // room for text, so its count is read from the legend instead.
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }: any) => {
                    if (!percent || percent < 0.07) return null;
                    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const rad = -midAngle * (Math.PI / 180);
                    return (
                      <text x={cx + r * Math.cos(rad)} y={cy + r * Math.sin(rad)}
                        fill="#fff" fontSize={13} fontWeight={700}
                        textAnchor="middle" dominantBaseline="central">
                        {value}
                      </text>
                    );
                  }}
                  innerRadius={34}
                  outerRadius={66}
                  paddingAngle={1}
                  dataKey="value"
                  onMouseEnter={(_: any, i: number) => setActiveSlice(i)}
                  onMouseLeave={() => setActiveSlice(null)}
                >
                  {/* Recharts 3 removed activeIndex/activeShape from <Pie>, so the highlight is
                      done on the Cell: the hovered slice keeps full colour and gains an outline
                      while the rest fade back. */}
                  {healthDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color}
                      opacity={activeSlice === null || activeSlice === index ? 1 : 0.3}
                      stroke={activeSlice === index ? '#37474f' : '#fff'}
                      strokeWidth={activeSlice === index ? 2 : 1} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: any, n: any) => [
                    `${v} meter${Number(v) === 1 ? '' : 's'}` +
                    (healthTotal ? ` · ${sharePct[healthDistribution.findIndex((sl) => sl.name === n)] || ''}` : ''), n]}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Legend as real rows: every count is readable even when the slice is a sliver, and
                hovering a row highlights its slice. Recharts' own <Legend> gave neither. */}
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {healthDistribution.map((sl, i) => (
                <Stack key={sl.name} direction="row" alignItems="center" spacing={1}
                  onMouseEnter={() => setActiveSlice(i)}
                  onMouseLeave={() => setActiveSlice(null)}
                  sx={{
                    px: 0.75, py: 0.25, borderRadius: 1, cursor: 'default',
                    bgcolor: activeSlice === i ? 'action.hover' : 'transparent',
                  }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: sl.color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.3 }}>{sl.name}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{sl.value}</Typography>
                  {!!healthTotal && (
                    <Typography variant="caption" color="text.secondary" sx={{ width: 52, textAlign: 'right' }}>
                      {sharePct[i]}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Meter Trends Histogram */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Meter Trends
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
              Based on Cal AI 7-point drift rule: 7+ consecutive events in same direction = drift
            </Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={driftDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="trend"
                  tick={{ fontSize: 10 }}
                  angle={-20}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  label={{ value: 'Number of Meters', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                />
                <Tooltip
                  formatter={(value: any, name: any, props: any) => [
                    `${value} meters`,
                    props.payload.description || 'Count'
                  ]}
                />
                <Bar
                  dataKey="value"
                  radius={[4, 4, 0, 0]}
                >
                  {driftDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Weekly Control Percentage */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Weekly Meter Health
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              % of meters healthy over the last 52 weeks. Each week carries forward every meter's
              most recent proving and judges it against the baseline in effect that week (stepped
              at maintenance resets). A meter is counted unhealthy if it is outside its control
              limits, failed repeatability, spiked, drifted past the threshold, or is biased.
              Overdue proving and a meter-factor adjustment do not count against it.
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeSeriesData} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={44}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  // Adaptive floor. A healthy fleet sits in a 1-2% band, and on a fixed 0-100 axis
                  // that renders as a flat block — which is how a meter awaiting re-prove stayed
                  // invisible. The floor never rises above 95, so a dip is shown in context rather
                  // than magnified into a cliff, and it falls away to 0 when health genuinely drops.
                  domain={[(dataMin: number) => Math.max(0, Math.min(95, Math.floor(dataMin - 1))), 100]}
                  allowDecimals={false}
                  tickFormatter={(value) => `${value}%`}
                  label={{ value: '% Healthy', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                />
                <Tooltip
                  content={({ active, payload }: any) => {
                    if (!active || !payload || !payload.length) return null;
                    const p = payload[0].payload;
                    if (p.controlPercentage == null) return null;
                    return (
                      <Paper sx={{ p: 1.25 }} elevation={4}>
                        <Typography variant="caption" color="text.secondary">
                          Week of {p.date}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{Number(p.controlPercentage).toFixed(1)}%</strong> healthy
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {p.healthy} of {p.active} meters healthy
                        </Typography>
                        {p.inControlPercentage != null && (
                          <Typography variant="caption" color="text.secondary">
                            {p.inControl} of {p.active} within control limits
                          </Typography>
                        )}
                      </Paper>
                    );
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="controlPercentage"
                  fill={COLORS.green}
                  fillOpacity={0.6}
                  stroke={COLORS.green}
                  strokeWidth={2}
                  connectNulls
                  name="% In Control"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Control Status Over Time */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Control Status Summary
            </Typography>
            <Box sx={{ mt: 3 }}>
              {data?.aggregate && (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Total Meters</Typography>
                    <Chip label={data.aggregate.total_meters} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="success.main">In Control</Typography>
                    <Chip
                      label={data.aggregate.meters_in_control}
                      size="small"
                      color="success"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="warning.main">Warning</Typography>
                    <Chip
                      label={data.aggregate.meters_warning}
                      size="small"
                      color="warning"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="error.main">Critical</Typography>
                    <Chip
                      label={data.aggregate.meters_critical}
                      size="small"
                      color="error"
                    />
                  </Box>
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AggregateHealthChart;